import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email, origin_url: window.location.origin });
      setSent(true);
    } catch {
      // The endpoint intentionally always succeeds; only network errors would land here.
      toast.error("Something went wrong. Please try again.");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7F5F0] px-6" data-testid="forgot-password-page">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-14 h-14 mx-auto rounded-full border-2 border-gold flex items-center justify-center mb-4">
            <KeyRound className="w-6 h-6 text-gold" strokeWidth={1.5} />
          </div>
          <div className="eyebrow">Account recovery</div>
          <h1 className="aperture-h1 text-3xl mt-2">Forgot your password?</h1>
          <p className="mt-3 text-sm text-navy/60">Enter your email and we'll send you a secure reset link.</p>
        </div>

        {sent ? (
          <div className="bg-white p-8 shadow-xl rounded-sm text-center" data-testid="forgot-password-sent">
            <div className="text-gold font-serif text-2xl mb-3">Check your email.</div>
            <p className="text-sm text-navy/70 leading-relaxed">
              If an account with that email exists, we've sent password reset instructions to it.
              The link will expire in <span className="text-navy font-medium">60 minutes</span>.
            </p>
            <p className="mt-4 text-xs text-navy/50">Didn't receive it? Check spam, or request another link in a few minutes.</p>
            <Link to="/login" className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-navy hover:text-gold transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white p-8 shadow-xl rounded-sm space-y-4">
            <div>
              <label className="eyebrow block mb-2">Email address</label>
              <input required type="email" value={email} onChange={(e)=>setEmail(e.target.value)}
                data-testid="forgot-password-email"
                className="w-full px-4 py-3 border rounded-sm text-sm focus:outline-none focus:border-gold" style={{ borderColor: "rgba(10,37,64,0.15)" }} />
            </div>
            <button disabled={busy} data-testid="forgot-password-submit" className="btn-primary w-full py-3 text-xs uppercase tracking-widest rounded-sm">
              {busy ? "Sending…" : "Send reset link"}
            </button>
            <Link to="/login" className="mt-2 flex items-center justify-center gap-2 text-xs uppercase tracking-widest text-navy/60 hover:text-gold transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
